import { Icon } from './Icon';
import { cadenceLabel, formatClockTime, relativeTime } from '../lib/format';

const FRESH_LABEL: Record<string, string> = { live: 'Live', fresh: 'Fresh', stale: 'Stale', very_stale: 'Stale' };

/** Freshness badge (spec §12.3): live < 5s, fresh < 15m, stale > 15m, very stale > 1h. */
export function FreshnessBadge({ freshness, compact }: { freshness?: { status: string; lastUpdatedAt: string } | null; compact?: boolean }) {
  if (!freshness) return null;
  const label = FRESH_LABEL[freshness.status] ?? freshness.status;
  return (
    <span className={`badge ${freshness.status}`} title={`${label} — last updated ${relativeTime(freshness.lastUpdatedAt)}`} aria-label={`${label}, last updated ${relativeTime(freshness.lastUpdatedAt)}`}>
      <span className="dot" aria-hidden />
      {label}
      {!compact && freshness.status !== 'live' && <span className="muted">· {relativeTime(freshness.lastUpdatedAt)}</span>}
    </span>
  );
}

interface Freshness {
  status: string;
  lastUpdatedAt: string;
  cadence?: 'realtime' | 'periodic';
  everySeconds?: number | null;
}

/**
 * Widget-header freshness: real-time data gets a pulsing "Live" mark, periodically refreshed data a
 * clock with how often it updates, and stale data a warning. The time of the last update shows next
 * to it (or in the tooltip when `compact`), in the reporting time zone.
 */
export function FreshnessIndicator({ freshness, compact }: { freshness?: Freshness | null; compact?: boolean }) {
  if (!freshness) return null;
  if (freshness.status === 'stale' || freshness.status === 'very_stale') return <FreshnessBadge freshness={freshness} compact={compact} />;
  const at = formatClockTime(freshness.lastUpdatedAt);
  if (freshness.cadence === 'realtime' || (!freshness.cadence && freshness.status === 'live')) {
    return (
      <span className="cadence live" role="img" title={`Real-time data — last update ${at}`} aria-label={`Live data, last update ${at}`}>
        <span className="live-dot" aria-hidden />
        <span className="cadence-text">Live</span>
        {!compact && <span className="cadence-at">{at}</span>}
      </span>
    );
  }
  if (!freshness.cadence) return null;
  const every = cadenceLabel(freshness.everySeconds);
  const how = every === 'Periodic' ? 'Updates periodically' : /^\d/.test(every) ? `Updates every ${every}` : `Updates ${every.toLowerCase()}`;
  return (
    <span className="cadence periodic" role="img" title={`${how} — last update ${at}`} aria-label={`${how}, last update ${at}`}>
      <Icon name="clock" size={12} />
      <span className="cadence-text">{every}</span>
      {!compact && <span className="cadence-at">{at}</span>}
    </span>
  );
}

const STATUS: Record<string, { cls: string; icon: string; label?: string }> = {
  completed: { cls: 'good', icon: 'check' },
  ok: { cls: 'good', icon: 'check', label: 'OK' },
  match: { cls: 'good', icon: 'check', label: 'Matches Shopify' },
  mismatch: { cls: 'critical', icon: 'alertCircle', label: 'Differs from Shopify' },
  unchecked: { cls: '', icon: 'clock', label: "Couldn't reach Shopify" },
  needs_permission: { cls: 'fresh', icon: 'alertCircle', label: 'Needs read_reports' },
  not_imported: { cls: '', icon: 'info', label: 'Not imported' },
  boom: { cls: 'good', icon: 'arrowUp', label: 'Taking off' },
  typical: { cls: '', icon: 'check', label: 'Typical' },
  flop: { cls: 'serious', icon: 'arrowDown', label: 'Fell flat' },
  too_early: { cls: '', icon: 'clock', label: 'Too early' },
  not_enough: { cls: '', icon: 'info', label: 'Needs more posts' },
  active: { cls: 'good', icon: 'check', label: 'Active' },
  within: { cls: 'good', icon: 'check', label: 'Within benchmark' },
  above: { cls: 'good', icon: 'arrowUp', label: 'Above benchmark' },
  running: { cls: 'fresh', icon: 'refresh', label: 'Running' },
  queued: { cls: '', icon: 'clock', label: 'Queued' },
  processed: { cls: 'good', icon: 'check', label: 'Processed' },
  warning: { cls: 'fresh', icon: 'alertCircle', label: 'Watch' },
  disconnected: { cls: '', icon: 'x', label: 'Disconnected' },
  ignored: { cls: '', icon: 'info', label: 'Ignored' },
  approved: { cls: '', icon: 'check' },
  pending_approval: { cls: 'fresh', icon: 'clock', label: 'Awaiting approval' },
  pending: { cls: 'fresh', icon: 'clock', label: 'Pending' },
  watch: { cls: 'fresh', icon: 'alertCircle', label: 'Watch' },
  medium: { cls: 'fresh', icon: 'alertCircle', label: 'Medium' },
  below: { cls: 'serious', icon: 'arrowDown', label: 'Below benchmark' },
  at_risk: { cls: 'serious', icon: 'alert', label: 'At risk' },
  high: { cls: 'serious', icon: 'alert', label: 'High' },
  cancelled: { cls: '', icon: 'x' },
  never: { cls: '', icon: 'clock', label: 'Not run yet' },
  none: { cls: '', icon: 'x', label: 'None' },
  failed: { cls: 'critical', icon: 'alertCircle' },
  critical: { cls: 'critical', icon: 'alertCircle', label: 'Critical' },
  needs_reauth: { cls: 'critical', icon: 'alertCircle', label: 'Needs attention' },
  revoked: { cls: 'critical', icon: 'x', label: 'Revoked' },
  expired: { cls: '', icon: 'clock', label: 'Expired' },
  refresh_failed: { cls: 'critical', icon: 'alertCircle', label: 'Refresh failed' },
};

export function StatusBadge({ status }: { status: string | null | undefined }) {
  if (!status) return <span className="muted">—</span>;
  const s = STATUS[status] ?? { cls: '', icon: 'info' };
  const label = s.label ?? status.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
  return (
    <span className={`badge ${s.cls}`}>
      <Icon name={s.icon} size={12} />
      {label}
    </span>
  );
}

export function Severity({ level }: { level: string }) {
  const icon = level === 'critical' ? 'alertCircle' : level === 'high' ? 'alert' : 'info';
  return (
    <span className={`sev ${level}`}>
      <Icon name={icon} size={14} />
      {level[0].toUpperCase() + level.slice(1)}
    </span>
  );
}
